import { ICalendarHeader, ICalendarItemData } from 'Calendar/types';
import { Avatar, Popover, Row } from 'antd';
import Icon from 'assets/Icons';
import Text from 'components/Text';
import { first, get, sortBy } from 'lodash';
import React, { CSSProperties, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FORMAT_TIME } from 'utils/format';
import { momentTimezone } from 'utils/time';
import Appointment from './Appointment';
import { calendarDOM } from 'Calendar/styles';
import { IAppointmentItemData } from 'types/appointment';
import { APPOINTMENT_SOURCE_TYPE } from 'constants/calendar';

const overlayInnerStyle: CSSProperties = { borderRadius: 0, boxShadow: 'none', padding: 0, background: 'transparent' };
type IMultiAppointmentProps = {
  data: ICalendarItemData[];
  headerData?: ICalendarHeader;
  isMonthView?: boolean;
  dateOfMonth?: string;
  disabled?: boolean;
};
const MultiAppointment: React.FC<IMultiAppointmentProps> = ({
  data = [],
  headerData,
  isMonthView,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
 
  const appointments = useMemo(() => sortBy(data, o => momentTimezone(o.startTime)), [data]);

  const content = useMemo(() => {
    const time = get(first(data), 'data.startTime');

    return (
      <PopoverAppointmentStyled>
        <button className="head" onClick={() => setOpen(false)}>
          <Icon type='close' />
        </button>
        <div className="body">
          <Text variant='H5' color='text_3' >Multi - Booking</Text>
          <div className="divider" />
          <div className='row-item'><Icon type='time' /> <Text variant='H9' color='text_3'>{time ? momentTimezone(time).format('MM-DD-YYYY HH:mm') : null}</Text></div>
          {headerData && headerData.type === 'staff' && <div className='row-item'>
            <Avatar size={'small'} style={{ backgroundColor: '#f56a00' }}>{headerData.label?.[0]}</Avatar> <Text variant='H9' color='text_3'>{headerData.label}</Text>
          </div>}
          <div className="list-item">
            {appointments.map(o => (
              <Appointment disabled={disabled} isMulti key={o.id} data={o} isMonthView={isMonthView} />
            ))}
          </div>
        </div>
      </PopoverAppointmentStyled>
    );
  }, [data, isMonthView, appointments]);

  return (
    <Popover
      arrow={false}
      placement="right"
      open={open}
      onOpenChange={setOpen}
      content={content}
      trigger="click"
      overlayInnerStyle={overlayInnerStyle}
      // @ts-ignore 
      getPopupContainer={() => calendarDOM.current}
    >
      <MultiAppointmentStyled
        className='MultiAppointmentStyled'
        onDoubleClick={(e: any) => e.stopPropagation()}
        onClick={((e) => {
          // e.preventDefault();
          e.stopPropagation(); setOpen(true);
        }) as React.MouseEventHandler<HTMLDivElement>}
      >
        <div className="appointment-box">
          {appointments.map((o, i) => (
            <React.Fragment key={o.id}>
              <AppointmentNew data={o} />
              {appointments.length - 1 !== i && <div className="divider" />}
            </React.Fragment>
          ))}
        </div>
        <div className="top appointment-alert-icon"><Icon type='exclamationCircle' /></div>
      </MultiAppointmentStyled>
    </Popover>

  );
};

export default MultiAppointment;

const AppointmentNew = ({ data }: { data: ICalendarItemData }) => {
  const time = useMemo(() => {
    const startTime = momentTimezone(data?.startTime);
    const endTime = momentTimezone(data?.endTime);
    return `${startTime.format(FORMAT_TIME)} - ${endTime.format(FORMAT_TIME)}`;
  }, [data]);
  const extraData = useMemo(() => {
    return data.data as IAppointmentItemData;
  }, [data]);
  return (
    <AppointmentNewContainer>
      <Row align={'middle'} wrap={false}>
        <div className='top-content text-ellipsis'>
          <Text className='customer-name appointment-name' variant='CONTENT_2' color='text_3' style={{ fontWeight: '600' }}>{extraData?.customerName?.slice(0, 11)}{extraData?.customerName.length > 11 ? '...' : ''}</Text>
          <Text className='time appointment-time text-ellipsis' variant='CAPTION_3' color='text_3' style={{ fontWeight: '600' }}>{time}</Text>
        </div>
        <SmallSvg style={{ display: 'flex', flexDirection: 'row', position: 'relative' }}>
          {extraData.requestStaff && <div className='appointment-icon-sm' style={{ display: 'flex', paddingTop: '1px', width: '16px' }}><RequestStaffIcon /></div>}
          {extraData.confirmed && <div className='appointment-icon-sm' style={{ display: 'flex', paddingTop: '1px', width: '16px' }}><ConfirmIcon /></div>}
        </SmallSvg>
      </Row>
      <div className='top-content text-ellipsis'>
        <Text className='time appointment-time service-name-list text-ellipsis' variant='CAPTION_3' color='text_3' style={{ color: '#1D2129', flex: 1 }}>{extraData.note ? 'Note: ' + extraData.note : extraData.services.map(o => o.serviceName || '').join(', ')}</Text>
        <SmallSvg className='appointment-icon-sm' style={{ width: '16px' }}>
          {extraData.sourceType !== APPOINTMENT_SOURCE_TYPE.CALl_IN ? <IconOnline /> : <IconCalling />}
        </SmallSvg>
      </div>
    </AppointmentNewContainer>
  );
};

const SmallSvg = styled.div`
gap: 2px;
display: flex;
align-items: center;
svg {
    width: 16px !important;
    height: 16px !important;
  }
`;

const AppointmentNewContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  .top-content {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    padding-right: 2px;
  }
  .customer-name {
     color: #1D2129;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    white-space: nowrap;  
  }
  .time.appointment-time-sm {
    color: #7B7B7B;
  }
  .service-name-list {
    white-space: nowrap;  
    padding-right: 2px;
    color: #1D2129;
  }
`;

const PopoverAppointmentStyled = styled.div`
    width: 50vh;
    max-width: 32rem;
    display: flex;
    flex-direction: column;
    padding:1.5rem;

    border-radius: 5px;
    border: 1px solid #86909C;
    background: #FFF;
    box-shadow: -1px 1px 4px 0px rgba(0, 0, 0, 0.10) inset, 0px 4px 4px 0px rgba(0, 0, 0, 0.25);

    .divider {
      background: #86909C;
      width: 100%;
      height: 1px;
    }

    .head {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 1.5rem;
    }

    .body {
      gap: 16px;
      display: flex;
      flex-direction: column;
    }
    .row-item {
      gap: 11px;
      display: flex;
      align-items: center;
    }
    .list-item {
      gap: 16px;
      display: flex;
      flex-direction: column;
    }
`;

type MultiAppointmentStyledProps = {}
const MultiAppointmentStyled = styled.div<MultiAppointmentStyledProps>`
  height: 100%;
  width: 100%;
  display: flex;

  .divider {
    background: #86909C;
    width: 100%;
    height: 0.5px;
    margin: 4px 0;
  }

  .appointment-box {
    border-radius: 5px;
    background: #FFEFEB;
  
    border: 0.5px solid #F05326;
    box-shadow: inset 3px 0px 0px 0px #F05326;
    overflow: hidden;

    padding: 4px 4px;
    padding-left: 8px;
    flex: 1;
  }
  .top {
    position: absolute;
    top: -10px;
    right:-10px;
    z-index: 1;
  }
  .customerName {
    color: #1D2129;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
  .time {
    color: #F05326;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

const ConfirmIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="12" fill="#148CF1" />
    <path d="M9.72692 18C9.40676 18 9.10261 17.8661 8.8785 17.6317L4.34817 12.8936C3.88394 12.4081 3.88394 11.6045 4.34817 11.1189C4.8124 10.6334 5.58078 10.6334 6.04501 11.1189L9.72692 14.9697L17.955 6.36414C18.4192 5.87862 19.1876 5.87862 19.6518 6.36414C20.1161 6.84967 20.1161 7.6533 19.6518 8.13882L10.5753 17.6317C10.3512 17.8661 10.0471 18 9.72692 18Z" fill="white" />
  </svg>
);
const RequestStaffIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="5" fill="#148CF1" />
    <path d="M4.5 9.85283C4.5 13.5 7.51457 15.4436 9.7213 17.1832C10.5 17.797 11.25 18.375 12 18.375C12.75 18.375 13.5 17.797 14.2787 17.1832C16.4854 15.4436 19.5 13.5 19.5 9.85283C19.5 6.20562 15.3748 3.6191 12 7.12547C8.62512 3.6191 4.5 6.20562 4.5 9.85283Z" fill="white" />
  </svg>
);

const IconCalling = ({ blue }: { blue?: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect width="24" height="24" rx="5" transform="matrix(1.19249e-08 -1 -1 -1.19249e-08 24 24)" fill={blue ? '#148CF1' : '#1D2129'} />
    <path d="M20.6777 10.6892C20.8949 10.5335 21.0219 10.2607 20.9969 9.99383C20.9045 9.16929 20.684 8.3591 20.3428 7.60265C19.5458 5.8273 18.0594 4.37294 16.2657 3.61723C15.5725 3.31428 14.8319 3.13612 14.0839 3.03379L13.8992 3.03379C13.5749 3.06913 13.2837 3.31907 13.2182 3.64189C13.142 3.94852 13.28 4.28975 13.5428 4.46276C13.7556 4.61478 14.0287 4.60632 14.2757 4.65233C15.4713 4.86251 16.6051 5.42423 17.4878 6.25835C18.5531 7.24707 19.2506 8.62156 19.4218 10.0649C19.4457 10.3329 19.5804 10.5994 19.8215 10.7326C20.0818 10.8898 20.4355 10.8732 20.6777 10.6892Z" fill="white" />
    <path d="M19.875 19.8616C20.1003 19.4961 20.214 19.0684 20.2169 18.6403C20.218 17.8669 20.218 17.0931 20.2169 16.3194C20.2147 15.9432 20.1371 15.5655 19.9659 15.2291C19.639 14.5555 18.9687 14.0685 18.2307 13.951C17.6016 13.8594 16.9766 13.7272 16.3766 13.5137C16.1583 13.4405 15.9411 13.3573 15.7125 13.3212C15.2469 13.2413 14.758 13.3076 14.3303 13.5082C13.8319 13.725 13.4917 14.1649 13.1038 14.5264C11.6546 13.6157 10.4218 12.37 9.49896 10.9297C9.80633 10.6087 10.159 10.3219 10.392 9.93801C10.7682 9.34721 10.8411 8.58303 10.6088 7.9245C10.4071 7.34916 10.2285 6.76277 10.1369 6.15872C10.0872 5.82485 10.0364 5.48326 9.8862 5.177C9.52915 4.38448 8.70019 3.84005 7.83221 3.8165C7.0095 3.81245 6.18643 3.8165 5.36372 3.81429C4.71292 3.80877 4.0658 4.0911 3.62629 4.5711C3.26813 4.95209 3.04911 5.45712 3.00825 5.97761C2.98359 6.21246 3.01966 6.44768 3.05132 6.67995C3.45844 10.037 4.97096 13.2329 7.24177 15.7304C8.86215 17.5113 10.8955 18.8987 13.1089 19.8359C14.3229 20.3302 15.5914 20.6928 16.8886 20.8872C17.2799 20.9317 17.6704 21.0315 18.0669 20.9902C18.8034 20.9302 19.4969 20.4966 19.875 19.8616ZM18.6378 18.7776C18.5833 19.1379 18.245 19.4298 17.8806 19.4317C14.4532 19.1008 11.1797 17.5025 8.76387 15.0561C6.53539 12.775 5.03722 9.77457 4.62274 6.60743C4.59255 6.36522 4.51746 6.10645 4.62384 5.87197C4.73758 5.57602 5.04936 5.37282 5.36556 5.38387C6.16324 5.3824 6.96054 5.38497 7.75822 5.3824C8.06668 5.39123 8.37294 5.58596 8.47969 5.88117C8.55368 6.09062 8.5662 6.31516 8.60706 6.53197C8.69025 7.01308 8.81246 7.48646 8.96043 7.95137C9.02338 8.14204 9.09553 8.33014 9.14853 8.52376C9.22215 8.79211 9.12608 9.09211 8.92546 9.28168C8.6019 9.60782 8.27429 9.92991 7.95221 10.2575C7.72214 10.4872 7.66104 10.8616 7.80312 11.1531C8.66006 12.6608 9.80559 14.0066 11.1705 15.0778C11.4414 15.3034 11.7381 15.4949 12.0219 15.7036C12.266 15.8674 12.5225 16.0117 12.771 16.1688C12.9919 16.3153 13.2863 16.3474 13.5297 16.2406C13.6865 16.1762 13.8039 16.0499 13.9206 15.9322C14.2033 15.6506 14.4805 15.3638 14.7646 15.0837C14.9189 14.9398 15.1283 14.8474 15.3415 14.8606C15.5123 14.8614 15.6691 14.9364 15.8292 14.985C16.4701 15.2088 17.1319 15.376 17.8044 15.4706C17.9782 15.5007 18.1618 15.5284 18.3069 15.6366C18.5277 15.786 18.6536 16.0536 18.6444 16.3179C18.6396 17.1377 18.6529 17.9582 18.6378 18.7776Z" fill="white" />
    <path d="M17.8358 10.2414C17.8652 10.1222 17.8663 9.99739 17.8394 9.87776C17.6528 8.96082 17.1912 8.1002 16.5202 7.44683C15.858 6.79087 14.9933 6.34584 14.0767 6.17872C13.871 6.15369 13.6527 6.20706 13.4903 6.33811C13.2897 6.49418 13.1734 6.75222 13.1925 7.00584C13.2087 7.28817 13.3906 7.55542 13.6505 7.66805C13.826 7.74462 14.0204 7.75787 14.1993 7.8245C15.2263 8.1499 16.0512 9.04585 16.2776 10.1008C16.3174 10.304 16.3965 10.5102 16.5596 10.6464C16.7742 10.8348 17.0959 10.8945 17.3613 10.7859C17.5965 10.6968 17.7791 10.4859 17.8358 10.2414Z" fill="white" />
  </svg>
);

const IconOnline = ({ blue }: { blue?: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect width="24" height="24" rx="5" fill={blue ? '#148CF1' : '#F05326'} />
    <path d="M11.8481 3H12.1174C13.4741 3.02215 14.8287 3.33996 16.0402 3.95484C18.0205 4.94836 19.612 6.69633 20.4027 8.76773C20.7827 9.74859 20.9761 10.7973 21 11.8481V12.1181C20.9771 13.6045 20.5936 15.0871 19.8627 16.3836C19.1849 17.5955 18.223 18.647 17.0773 19.4317C15.6299 20.434 13.8787 20.9757 12.1206 21H11.8541C10.232 20.9705 8.61551 20.5078 7.24195 19.6405C6.20836 18.995 5.30766 18.1383 4.61121 17.1384C3.58184 15.6795 3.02461 13.9041 3 12.1209V11.8548C3.03023 10.0703 3.59238 8.29523 4.62738 6.83836C5.48414 5.61633 6.64781 4.6098 7.98375 3.94289C9.17871 3.34066 10.5118 3.02813 11.8481 3ZM10.2499 4.90582C9.53062 5.64586 9.08062 6.59684 8.73187 7.55625C9.17695 7.59598 9.62133 7.63957 10.0661 7.67789C10.5343 7.70074 11.003 7.71867 11.4716 7.73941C11.4716 6.53637 11.4716 5.33332 11.4716 4.13027C11.0019 4.2709 10.5899 4.55812 10.2499 4.90582ZM12.5273 4.12992C12.5273 5.33402 12.5273 6.53848 12.5273 7.74293C12.9946 7.725 13.4621 7.70988 13.9294 7.6902C14.3783 7.65504 14.8269 7.61637 15.2755 7.57875C15.0259 6.89039 14.7316 6.21152 14.3161 5.60578C13.8795 4.95926 13.2927 4.36055 12.5273 4.12992ZM5.82727 6.99902C6.1159 7.07145 6.40418 7.14668 6.69352 7.21699C7.01801 7.28379 7.34391 7.3432 7.6691 7.40613C7.90324 6.70195 8.19047 6.01113 8.58246 5.37867C8.73223 5.09918 8.93438 4.85309 9.11508 4.59363C7.83469 5.09285 6.69492 5.93449 5.82727 6.99902ZM14.8849 4.59363C15.0663 4.85414 15.2692 5.10129 15.4196 5.38219C15.8166 6.02344 16.1048 6.7248 16.3425 7.43848C16.6628 7.37941 16.9837 7.32387 17.304 7.26305C17.6071 7.19484 17.9087 7.11891 18.2111 7.04719C17.3395 5.95945 16.1843 5.10023 14.8849 4.59363ZM5.18777 7.90781C4.53984 8.9857 4.15699 10.2179 4.07367 11.4727C5.04715 11.4727 6.02098 11.473 6.9948 11.4723C7.03066 10.4482 7.15441 9.42727 7.38293 8.42812C6.64184 8.2977 5.90602 8.13281 5.18777 7.90781ZM16.6245 8.46117C16.8473 9.44977 16.9704 10.4588 17.0052 11.4713C17.979 11.472 18.9529 11.4716 19.9263 11.4713C19.8441 10.239 19.4735 9.02789 18.8456 7.96371C18.1172 8.17852 17.3733 8.33707 16.6245 8.46117ZM8.05055 11.4713C9.19102 11.4716 10.3311 11.4716 11.4716 11.4716C11.4716 10.5793 11.472 9.68742 11.4713 8.79551C10.4556 8.77195 9.44062 8.70621 8.43199 8.58246C8.20699 9.52957 8.08781 10.4992 8.05055 11.4713ZM12.5288 8.79867C12.528 9.68988 12.5284 10.5807 12.5284 11.4716C13.6689 11.4716 14.809 11.4716 15.9495 11.4713C15.915 10.5073 15.793 9.5468 15.575 8.60672C14.5636 8.72062 13.5465 8.7825 12.5288 8.79867ZM4.07367 12.5273C4.15559 13.7441 4.51629 14.9405 5.13047 15.9945C5.86664 15.7733 6.62004 15.6148 7.37766 15.4868C7.15723 14.5151 7.03559 13.523 6.99691 12.5277C6.02238 12.527 5.04785 12.5273 4.07367 12.5273ZM8.05266 12.5288C8.09238 13.4752 8.20945 14.4184 8.42812 15.3405C9.43887 15.2266 10.4549 15.1644 11.4716 15.1482C11.4741 14.275 11.472 13.4017 11.4727 12.5284C10.3325 12.5284 9.19277 12.528 8.05266 12.5288ZM12.5284 12.5284C12.5288 13.4027 12.528 14.2767 12.5288 15.1511C13.5444 15.1714 14.5587 15.2404 15.567 15.3616C15.787 14.4325 15.9087 13.4822 15.9473 12.5288C14.8076 12.5284 13.6682 12.5284 12.5284 12.5284ZM17.0031 12.5288C16.9669 13.5346 16.8361 14.5355 16.6181 15.5177C17.3694 15.6457 18.114 15.8148 18.8414 16.0426C19.4725 14.9774 19.8437 13.7634 19.9263 12.5288C18.9521 12.5284 17.9776 12.528 17.0031 12.5288ZM10.4876 16.2395C9.89906 16.2532 9.31441 16.3253 8.72766 16.3692C9.02648 17.1936 9.39563 18.0068 9.95004 18.6934C10.3448 19.1905 10.8543 19.6233 11.4716 19.8086C11.4716 18.6069 11.4716 17.4049 11.4713 16.2033C11.1432 16.2156 10.8156 16.2275 10.4876 16.2395ZM12.5288 16.2068C12.528 17.4074 12.5284 18.608 12.5284 19.8082C13.2716 19.5853 13.8454 19.0119 14.2774 18.39C14.7063 17.7772 15.0108 17.0885 15.266 16.3882C14.822 16.3478 14.3787 16.3056 13.9346 16.2673C13.466 16.2448 12.9974 16.2272 12.5288 16.2068ZM6.65906 16.6948C6.35742 16.7651 6.05684 16.8396 5.7559 16.912C6.64852 18.0388 7.84242 18.9251 9.18785 19.4341C8.48613 18.577 8.00449 17.5607 7.66453 16.512C7.32844 16.5686 6.99375 16.6318 6.65906 16.6948ZM16.3302 16.5373C15.986 17.5758 15.5093 18.5841 14.8121 19.4341C16.1404 18.9311 17.3223 18.0616 18.21 16.9539C17.9164 16.8793 17.6236 16.8034 17.3297 16.7306C16.9971 16.6628 16.6631 16.6027 16.3302 16.5373Z" fill="white" />
  </svg>
);
